import { ActionIcon, Card, Group, Menu, Skeleton, Stack, Text, Tooltip } from '@mantine/core'
import { IconAlertTriangle, IconDots, IconEyeOff, IconRefresh } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { ApiError } from '../api/client'
import { fetchWidgetData } from '../api/dashboard'
import type { WidgetDefinition } from '../api/types'
import { rendererFor } from '../widgets/registry'

/**
 * One widget: its own data fetch, its own loading and error states.
 *
 * Fetching per widget rather than in the dashboard is what stops one slow or
 * broken module from taking down the whole screen - a failing widget shows an
 * error in its own frame while everything around it works.
 */
export function WidgetCard({
  definition,
  onHide,
}: {
  definition: WidgetDefinition
  onHide: () => void
}) {
  const query = useQuery({
    queryKey: ['widget', definition.id] as const,
    queryFn: ({ signal }) => fetchWidgetData<never>(definition.id, signal),
    refetchInterval: definition.refresh_seconds ? definition.refresh_seconds * 1000 : false,
  })

  const Renderer = rendererFor(definition.id)

  return (
    <Card padding="sm" radius="md" h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Only this header drags, so selecting text in the body does not fling
          the widget across the grid. */}
      <Card.Section
        withBorder
        inheritPadding
        py="xs"
        className="medulla-widget-handle"
      >
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} truncate="end">
              {definition.title}
            </Text>
            {query.data?.headline && (
              <Text size="xs" c="dimmed" truncate="end">
                {query.data.headline}
              </Text>
            )}
          </Group>

          <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
            {/*
              No spinner while refetching. A widget refreshing in the
              background is not something anybody needs told about, and a
              circle that appears for 60ms on every revalidation is the exact
              thing that makes an application feel like a web page.
            */}
            <Menu position="bottom-end" withArrow width={180}>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm" aria-label={`${definition.title} options`}>
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconRefresh size={14} />} onClick={() => query.refetch()}>
                  Refresh
                </Menu.Item>
                <Menu.Item leftSection={<IconEyeOff size={14} />} onClick={onHide}>
                  Hide widget
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </Card.Section>

      <Card.Section inheritPadding py="sm" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {query.isPending ? (
          <Stack gap={6}>
            <Skeleton height={12} width="60%" />
            <Skeleton height={12} />
            <Skeleton height={12} width="80%" />
          </Stack>
        ) : query.isError ? (
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <IconAlertTriangle size={16} color="var(--mantine-color-orange-6)" />
            <Text size="sm" c="dimmed">
              {query.error instanceof ApiError && query.error.status === 404
                ? 'This widget is no longer available to you.'
                : 'Could not load this widget.'}
            </Text>
          </Group>
        ) : (
          <Renderer definition={definition} payload={query.data} />
        )}
      </Card.Section>

      {query.data && (
        <Card.Section inheritPadding pb="xs">
          <Tooltip label={new Date(query.data.generated_at).toLocaleString()} openDelay={400}>
            <Text size="10px" c="dimmed">
              Updated {new Date(query.data.generated_at).toLocaleTimeString()}
            </Text>
          </Tooltip>
        </Card.Section>
      )}
    </Card>
  )
}
